import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * IconButton — przycisk czysto ikonowy (X-zamknij, akcje wierszowe, toggle).
 *
 * Luka DS: `Button` jest tekstowy (`px-4 py-2` + children), więc setki
 * przycisków ikonowych w apce budowano surowym `<button>` z ręcznymi klasami.
 * Ten prymityw daje im spójny rozmiar/hover/focus i WYMUSZA `aria-label`
 * (przycisk ikonowy bez etykiety jest niedostępny dla czytników ekranu).
 *
 * Ikona jako children (dowolny <svg>, np. lucide) — rozmiar dostraja `size`
 * przez selektor `[&_svg]`, więc nie trzeba ręcznie ustawiać wymiarów ikony.
 */
const iconButtonVariants = cva(
  cn(
    'inline-flex items-center justify-center rounded-lg transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ),
  {
    variants: {
      variant: {
        ghost: 'bg-transparent text-text-secondary hover:bg-bg-tertiary hover:text-text-primary',
        subtle:
          'bg-bg-tertiary text-text-secondary hover:bg-bg-secondary hover:text-text-primary border border-border-primary',
        danger: 'bg-transparent text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20',
      },
      size: {
        sm: 'h-7 w-7 [&_svg]:h-4 [&_svg]:w-4',
        md: 'h-9 w-9 [&_svg]:h-5 [&_svg]:w-5',
      },
    },
    defaultVariants: { variant: 'ghost', size: 'md' },
  },
);

export type IconButtonVariant = NonNullable<VariantProps<typeof iconButtonVariants>['variant']>;
export type IconButtonSize = NonNullable<VariantProps<typeof iconButtonVariants>['size']>;

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label'
> {
  /** Ikona do wyświetlenia (np. <X /> z lucide). */
  children: ReactNode;
  /** Etykieta dla czytników ekranu — WYMAGANA (przycisk nie ma widocznego tekstu). */
  'aria-label': string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { variant = 'ghost', size = 'md', className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...rest}
    >
      {children}
    </button>
  );
});
